import { useState } from 'react';

import type { CreateMcpConnectorInput } from '../types';

export interface Step2ConnectionProps {
  value: Partial<CreateMcpConnectorInput>;
  onBack: () => void;
  onNext: (data: Pick<CreateMcpConnectorInput, 'serverUrl' | 'authMethod' | 'apiKey'>) => void;
}

/**
 * Sprint A4 S1 PR 3 — Task 8. Step 2 do wizard: serverUrl + auth + apiKey.
 *
 * v1: apenas API Key (select disabled). OAuth/JWT em sprints futuras.
 * apiKey masked com toggle mostrar/ocultar; aviso explícito de cofre
 * antes de salvar (operador deve ter cópia da chave fora do Lume).
 */
export function McpConnectorStep2Connection({
  value,
  onBack,
  onNext,
}: Step2ConnectionProps): JSX.Element {
  const [serverUrl, setServerUrl] = useState(value.serverUrl ?? '');
  const [apiKey, setApiKey] = useState(value.apiKey ?? '');
  const [showKey, setShowKey] = useState(false);

  const urlValid = /^https?:\/\//.test(serverUrl);
  const canProceed = urlValid && apiKey.length >= 8;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <label htmlFor="step2-serverUrl" className="block text-sm font-medium text-text-primary">
          URL do servidor MCP *
        </label>
        <input
          id="step2-serverUrl"
          type="url"
          value={serverUrl}
          onChange={(e) => setServerUrl(e.target.value)}
          placeholder="https://erp.example.com/mcp"
          className="mt-1 w-full rounded border border-border-subtle px-3 py-2 font-mono text-sm"
        />
        <p className="mt-1 text-xs text-text-secondary">URL completa fornecida pelo seu ERP/CRM.</p>
        {!urlValid && serverUrl.length > 0 && (
          <p className="mt-1 text-xs text-red-600">URL deve começar com http:// ou https://</p>
        )}
      </div>

      <div>
        <label htmlFor="step2-authMethod" className="block text-sm font-medium text-text-primary">
          Método de autenticação
        </label>
        <select
          id="step2-authMethod"
          disabled
          className="mt-1 w-full rounded border border-border-subtle bg-bg-muted px-3 py-2 text-sm"
        >
          <option>API Key</option>
        </select>
        <p className="mt-1 text-xs text-text-secondary">
          v1 suporta apenas API Key. OAuth/JWT em sprints futuras.
        </p>
      </div>

      <div>
        <label htmlFor="step2-apiKey" className="block text-sm font-medium text-text-primary">
          Chave de API *
        </label>
        <div className="mt-1 flex gap-2">
          <input
            id="step2-apiKey"
            type={showKey ? 'text' : 'password'}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="flex-1 rounded border border-border-subtle px-3 py-2 font-mono text-sm"
          />
          <button
            type="button"
            onClick={() => setShowKey(!showKey)}
            className="rounded border border-border-subtle px-3 py-2 text-xs hover:bg-bg-muted"
          >
            {showKey ? '🙈 Ocultar' : '👁 Mostrar'}
          </button>
        </div>
        <div className="mt-2 rounded bg-amber-50 p-3 text-xs text-amber-900">
          <strong>⚠ Importante:</strong> a chave será armazenada criptografada no cofre. Não é
          possível recuperá-la depois — guarde uma cópia segura se precisar.
        </div>
      </div>

      <div className="mt-4 flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="rounded border border-border-subtle px-4 py-2 text-sm hover:bg-bg-muted"
        >
          ← Voltar
        </button>
        <button
          type="button"
          onClick={() => onNext({ serverUrl, authMethod: 'api-key', apiKey })}
          disabled={!canProceed}
          className="rounded bg-accent px-4 py-2 text-sm text-white hover:bg-accent-hover disabled:opacity-50"
        >
          Próximo →
        </button>
      </div>
    </div>
  );
}
